import { memo, useCallback, useRef, useState } from 'react'
import { Box, Button, CircularProgress, IconButton, InputBase, LinearProgress, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import { useDispatch, useSelector } from 'react-redux'
import { FeedbackCategory } from '../../proto/generated/user-feedback'
import {
    addAttachment,
    FeedbackMediaAttachment,
    MAX_ATTACHMENTS,
    removeAttachment,
    requestCapture,
    resetFeedbackDraft,
    setDescription
} from '../../reducer/feedback.reducer'
import { selectFeedbackStore } from '../../selectors/feedback'
import { MAX_VIDEO_SECONDS } from '../../util/media'
import { submitFeedback, uploadAttachments } from '../../util/uploadMedia'
import { NonInteractiveTooltip } from '../custom'
import ClearIcon from '../../assets/icons/ClearIcon'
import TickIcon from '../../assets/icons/TickIcon'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import SupportHeader from './SupportHeader'
import SupportScrollArea from './SupportScrollArea'
import SectionLabel from './SectionLabel'

const ACCEPTED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'video/webm', 'video/mp4', 'video/quicktime']

type Props = {
    onBack: () => void
    onDone: () => void
}

function MediaThumbnail({
    attachment,
    index,
    progress,
    onRemove
}: {
    attachment: FeedbackMediaAttachment
    index: number
    progress: number | null
    onRemove: (index: number) => void
}) {
    const isUploading = progress !== null && progress < 100
    const isVideo = attachment.type === 'video'
    return (
        <Box
            sx={{
                position: 'relative',
                aspectRatio: '16 / 9',
                borderRadius: 1,
                overflow: 'hidden',
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.default'
            }}
        >
            <Box
                component={isVideo ? 'video' : 'img'}
                src={attachment.url}
                alt={isVideo ? undefined : `Screenshot ${index + 1}`}
                {...(isVideo ? { muted: true, playsInline: true } : {})}
                sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    opacity: isUploading ? 0.5 : 1,
                    transition: 'opacity 0.2s'
                }}
            />
            {isVideo ? (
                <Typography
                    variant="caption"
                    sx={(theme) => ({
                        position: 'absolute',
                        left: 4,
                        bottom: 4,
                        px: 0.5,
                        borderRadius: 0.5,
                        fontSize: 10,
                        lineHeight: '16px',
                        bgcolor: alpha(theme.palette.common.black, 0.6),
                        color: theme.palette.common.white
                    })}
                >
                    Video
                </Typography>
            ) : null}
            {progress !== null ? (
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 3 }}
                />
            ) : null}
            {!isUploading ? (
                <IconButton
                    size="small"
                    onClick={() => onRemove(index)}
                    aria-label={`Remove ${attachment.type}`}
                    sx={(theme) => ({
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        width: 20,
                        height: 20,
                        p: 0,
                        color: 'text.primary',
                        bgcolor: alpha(theme.palette.background.paper, 0.92),
                        border: `1px solid ${theme.palette.divider}`,
                        '&:hover': { bgcolor: theme.palette.background.paper }
                    })}
                >
                    <ClearIcon sx={{ fontSize: 12 }} />
                </IconButton>
            ) : null}
        </Box>
    )
}

function AttachButton({
    icon,
    label,
    tooltip,
    onClick,
    disabled
}: {
    icon: React.ReactNode
    label: string
    tooltip: string
    onClick: () => void
    disabled: boolean
}) {
    return (
        <NonInteractiveTooltip arrow placement="top" title={tooltip}>
            <span>
                <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={onClick}
                    disabled={disabled}
                    startIcon={icon}
                    sx={{
                        height: 30,
                        px: 1.25,
                        fontSize: 12.5,
                        fontWeight: 400,
                        color: 'text.secondary',
                        '& .MuiButton-startIcon': { mr: 0.75, '& svg': { fontSize: 16 } },
                        '&:hover': { color: 'text.primary', bgcolor: 'action.hover' }
                    }}
                >
                    {label}
                </Button>
            </span>
        </NonInteractiveTooltip>
    )
}

function SentState({ isBug, onSendAnother, onDone }: { isBug: boolean; onSendAnother: () => void; onDone: () => void }) {
    return (
        <Stack alignItems="center" justifyContent="center" textAlign="center" sx={{ flex: 1, minHeight: 0, px: 3, py: 4 }}>
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                    '& svg': { width: 22, height: 22 }
                }}
            >
                <TickIcon />
            </Box>
            <Typography variant="subtitle2" fontWeight={500} sx={{ mt: 1.5 }}>
                Thanks, we've got it.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 280, lineHeight: 1.55 }}>
                {isBug
                    ? "We'll look into it. If we need more detail, we'll reach out on your account email."
                    : 'We read every note and use it to decide what to build next.'}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
                <Button variant="outlined" color="inherit" size="small" onClick={onSendAnother} sx={{ height: 30 }}>
                    Send another
                </Button>
                <Button variant="contained" size="small" disableElevation onClick={onDone} sx={{ height: 30 }}>
                    Done
                </Button>
            </Stack>
        </Stack>
    )
}

/** Bug report / feedback composer inside the Help & feedback tab. The draft lives in Redux. */
function FeedbackFormInner({ onBack, onDone }: Props) {
    const dispatch = useDispatch()
    const { category, description, attachments, isCapturing, captureError } = useSelector(selectFeedbackStore)
    const isBug = category === FeedbackCategory.BUG

    const [uploadProgress, setUploadProgress] = useState<number[] | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [submitted, setSubmitted] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const isUploading = uploadProgress !== null
    const isBusy = isCapturing || isUploading || isSubmitting
    const atLimit = attachments.length >= MAX_ATTACHMENTS
    const canSend = description.trim().length > 0 && !isBusy
    const limitTooltip = `Up to ${MAX_ATTACHMENTS} attachments`

    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files ?? [])
            const remaining = MAX_ATTACHMENTS - attachments.length
            files
                .filter((f) => ACCEPTED_FILE_TYPES.includes(f.type))
                .slice(0, Math.max(0, remaining))
                .forEach((file) => {
                    dispatch(
                        addAttachment({
                            type: file.type.startsWith('video/') ? 'video' : 'screenshot',
                            url: URL.createObjectURL(file)
                        })
                    )
                })
            e.target.value = ''
        },
        [attachments.length, dispatch]
    )

    const handleRemoveAttachment = useCallback((index: number) => dispatch(removeAttachment(index)), [dispatch])

    const handleSend = useCallback(async () => {
        if (!canSend) return
        setSubmitError(null)
        let uploadResults: Awaited<ReturnType<typeof uploadAttachments>> = []
        try {
            if (attachments.length > 0) {
                setUploadProgress(attachments.map(() => 0))
                uploadResults = await uploadAttachments(attachments, (index, pct) => {
                    setUploadProgress((prev) => {
                        if (!prev) return prev
                        const next = [...prev]
                        next[index] = pct
                        return next
                    })
                })
            }
            setUploadProgress(null)
            setIsSubmitting(true)
            await submitFeedback(category, description, uploadResults)
            AnalyticsManager.logEvent('Feedback Submitted', { category: isBug ? 'bug' : 'feedback', attachments: attachments.length })
            dispatch(resetFeedbackDraft())
            setSubmitted(true)
        } catch (err) {
            setSubmitError(isBug ? "Couldn't send the report. Please try again." : "Couldn't send your feedback. Please try again.")
        } finally {
            setUploadProgress(null)
            setIsSubmitting(false)
        }
    }, [attachments, canSend, category, description, dispatch, isBug])

    const title = isBug ? 'Report a bug' : 'Share feedback'

    if (submitted) {
        return (
            <>
                <SupportHeader title={title} onBack={onBack} />
                <SentState isBug={isBug} onSendAnother={() => setSubmitted(false)} onDone={onDone} />
            </>
        )
    }

    const errorText = captureError ?? submitError

    return (
        <>
            <SupportHeader title={title} onBack={onBack} />
            <SupportScrollArea>
                <Box sx={{ px: 2, py: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.55 }}>
                        {isBug
                            ? 'Tell us what you expected and what happened instead. A screenshot or a short recording helps a lot.'
                            : 'What would make Tick2Trade better for you? Small details are welcome.'}
                    </Typography>
                    <Box
                        sx={{
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 2,
                            px: 1.5,
                            py: 1.25,
                            bgcolor: 'background.default',
                            transition: 'border-color 0.15s',
                            '&:focus-within': { borderColor: 'primary.main' }
                        }}
                    >
                        <InputBase
                            fullWidth
                            multiline
                            autoFocus
                            minRows={5}
                            maxRows={12}
                            placeholder={isBug ? 'What happened, and how can we reproduce it?' : 'What would you change or add?'}
                            value={description}
                            onChange={(e) => dispatch(setDescription(e.target.value))}
                            disabled={isUploading || isSubmitting}
                            inputProps={{ 'aria-label': isBug ? 'Bug description' : 'Feedback' }}
                            sx={{
                                p: 0,
                                fontSize: '0.875rem',
                                lineHeight: 1.5,
                                '& textarea': { resize: 'none', p: 0 }
                            }}
                        />
                    </Box>

                    <Box sx={{ mt: 2.5 }}>
                        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
                            <SectionLabel sx={{ mb: 0 }}>Attachments</SectionLabel>
                            <Typography variant="caption" color="text.tertiary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                {attachments.length}/{MAX_ATTACHMENTS}
                            </Typography>
                        </Stack>
                        {attachments.length > 0 ? (
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 1.25 }}>
                                {attachments.map((attachment, index) => (
                                    <MediaThumbnail
                                        key={`${index}-${attachment.url}`}
                                        attachment={attachment}
                                        index={index}
                                        progress={uploadProgress ? uploadProgress[index] ?? 0 : null}
                                        onRemove={handleRemoveAttachment}
                                    />
                                ))}
                            </Box>
                        ) : null}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={ACCEPTED_FILE_TYPES.join(',')}
                            multiple
                            hidden
                            onChange={handleFileChange}
                        />
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                            <AttachButton
                                icon={<PhotoCameraOutlinedIcon />}
                                label="Screenshot"
                                tooltip={atLimit ? limitTooltip : 'Capture the screen'}
                                onClick={() => dispatch(requestCapture('screenshot'))}
                                disabled={isBusy || atLimit}
                            />
                            <AttachButton
                                icon={<VideocamOutlinedIcon />}
                                label="Record"
                                tooltip={atLimit ? limitTooltip : `Record the screen (up to ${Math.round(MAX_VIDEO_SECONDS / 60)} min)`}
                                onClick={() => dispatch(requestCapture('video'))}
                                disabled={isBusy || atLimit}
                            />
                            <AttachButton
                                icon={<AttachFileIcon />}
                                label="Upload"
                                tooltip={atLimit ? limitTooltip : 'Images or videos from your device'}
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isBusy || atLimit}
                            />
                        </Stack>
                    </Box>

                    {errorText ? (
                        <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1.5 }} role="alert">
                            {errorText}
                        </Typography>
                    ) : null}
                </Box>
            </SupportScrollArea>
            <Box sx={{ flexShrink: 0, px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
                <NonInteractiveTooltip
                    arrow
                    placement="top"
                    title={!description.trim() ? 'Add a description first' : ''}
                    disableHoverListener={!!description.trim()}
                >
                    <span style={{ display: 'block' }}>
                        <Button
                            variant="contained"
                            fullWidth
                            disableElevation
                            disabled={!canSend}
                            onClick={handleSend}
                            data-event={isBug ? 'Bug Report Sent' : 'Feedback Sent'}
                            sx={{ height: 34, fontWeight: 500 }}
                        >
                            {isUploading || isSubmitting ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : isBug ? (
                                'Send bug report'
                            ) : (
                                'Send feedback'
                            )}
                        </Button>
                    </span>
                </NonInteractiveTooltip>
            </Box>
        </>
    )
}

export default memo(FeedbackFormInner)
